import { Box, Typography } from '@mui/material'
import { palette } from '../theme/tokens.js'

/**
 * Les produits en alerte, chacun face à SON seuil.
 *
 * ⭐ La carte donne un nombre ; ce graphe dit lequel manque le plus. Une barre
 * par article, longue de son stock, et un repère vertical au seuil d'alerte :
 * l'écart entre les deux se lit sans calcul.
 *
 * ⚠️ Ici la couleur porte un STATUT (rupture, alerte) — c'est l'usage que le
 * thème lui réserve —, et jamais seule : chaque ligne l'écrit en toutes
 * lettres à côté du nom.
 */

export interface ArticleEnAlerte {
  id: string
  nom: string
  stock: number
  seuil: number
  etat: 'rupture' | 'alerte'
}

/** Au-delà, la liste ne se lit plus : le catalogue filtré prend le relais. */
const LIGNES_MAX = 20

export function GrapheStock({ articles }: { articles: readonly ArticleEnAlerte[] }): JSX.Element {
  if (articles.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">Aucun produit sous son seuil.</Typography>
      </Box>
    )
  }

  // Les ruptures d'abord, puis du plus entamé au moins entamé.
  const tries = [...articles].sort((a, b) => {
    if (a.etat !== b.etat) return a.etat === 'rupture' ? -1 : 1
    return a.stock / Math.max(1, a.seuil) - b.stock / Math.max(1, b.seuil)
  })
  const visibles = tries.slice(0, LIGNES_MAX)
  const echelle = Math.max(1, ...visibles.map((a) => Math.max(a.stock, a.seuil))) * 1.1
  const pct = (v: number): string => `${(Math.max(0, v) / echelle) * 100}%`

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {visibles.map((a) => {
        const couleur = a.etat === 'rupture' ? palette.danger[500] : palette.warning[500]
        return (
          <Box
            key={a.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 2fr) minmax(0, 3fr)' },
              gap: { xs: 0.5, sm: 2 },
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap title={a.nom} sx={{ minWidth: 0 }}>
                {a.nom}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  flexShrink: 0,
                  fontWeight: 700,
                  color: a.etat === 'rupture' ? palette.danger[700] : palette.warning[700],
                }}
              >
                {a.etat === 'rupture' ? 'Rupture' : 'Alerte'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ position: 'relative', flex: 1, height: 12 }}>
                {/* La barre : le stock, extrémité arrondie, base carrée. */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: pct(a.stock),
                    bgcolor: couleur,
                    borderRadius: '0 4px 4px 0',
                  }}
                />
                {/* Le repère : le seuil d'alerte. */}
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    left: pct(a.seuil),
                    top: -3,
                    bottom: -3,
                    width: 2,
                    bgcolor: 'text.primary',
                    borderRadius: 1,
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ flexShrink: 0, minWidth: 72, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
              >
                <b>{a.stock}</b> / {a.seuil}
              </Typography>
            </Box>
          </Box>
        )
      })}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Box sx={{ width: 2, height: 12, bgcolor: 'text.primary', borderRadius: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Seuil d’alerte — chiffres : stock / seuil
          {tries.length > LIGNES_MAX ? ` · ${tries.length - LIGNES_MAX} autre(s) au catalogue` : ''}
        </Typography>
      </Box>
    </Box>
  )
}
